import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../axiosInstance';

export const useComments = ({ persona, requestId, initialFilters = {}, skipInitialFetch = false }) => {
  const addCommentLink = persona === "customer" ? "enduser" : "";

  const [prevComments, setPrevComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  
  // Track if this is the first render
  const isFirstRender = useRef(true);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // Sorting state
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Filter state with initial filters
  const [filters, setFilters] = useState({
    search: '',
    userId: null,
    memberId: null,
    subFormId: null,
    hasReview: null,
    ...initialFilters,
  });

  // Fetch comments function
  const fetchComments = useCallback(async (page = 1, overrideParams = {}, silent = false) => {
    if (!requestId) return;

    if (!silent) setLoadingComments(true);
    try {
      // Use the appropriate endpoint based on persona
      const commentsEndpoint = persona === "customer" 
        ? `/api/comments/enduser/${requestId}` 
        : `/api/comments/${requestId}`;
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', overrideParams.limit || pagination.limit);
      params.append('sortBy', overrideParams.sortBy || sortBy);
      params.append('sortOrder', overrideParams.sortOrder || sortOrder);
      
      // Add filters if they have values
      const currentFilters = overrideParams.filters || filters;
      if (currentFilters.search) params.append('search', currentFilters.search);
      if (currentFilters.userId) params.append('userId', currentFilters.userId);
      if (currentFilters.memberId) params.append('memberId', currentFilters.memberId);
      if (currentFilters.subFormId) params.append('subFormId', currentFilters.subFormId);
      if (currentFilters.hasReview !== null) params.append('hasReview', currentFilters.hasReview);
      
      const resp = await axiosInstance.get(`${commentsEndpoint}?${params.toString()}`);
      
      // Extract comments and pagination data from the response
      if (resp?.data) {
        // Handle different response structures
        const commentsData = resp.data.data || [];
        setPrevComments(commentsData);
        
        // Update pagination info if available
        if (resp.data.pagination) {
          setPagination(resp.data.pagination);
        }
      }
    } catch (err) {
      console.error('Error fetching comments:', err.message);
    } finally {
      if (!silent) setLoadingComments(false);
    }
  }, [requestId, persona, filters, pagination.limit, sortBy, sortOrder]);

  // Effect to fetch comments when parameters change
  useEffect(() => {
    // Skip fetch if explicitly requested or if it's the first render and skipInitialFetch is true
    if (!requestId || (isFirstRender.current && skipInitialFetch)) {
      isFirstRender.current = false;
      return;
    }
    
    isFirstRender.current = false;
    fetchComments(1, { limit: pagination.limit, sortBy, sortOrder, filters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, persona, pagination.limit, sortBy, sortOrder, filters.search, filters.userId, filters.memberId, filters.subFormId, filters.hasReview]);

  // Pagination handlers
  const goToPage = useCallback((page) => {
    fetchComments(page, { limit: pagination.limit, sortBy, sortOrder, filters });
  }, [fetchComments, pagination.limit, sortBy, sortOrder, filters]);

  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      goToPage(pagination.currentPage + 1);
    }
  }, [pagination, goToPage]);

  const prevPage = useCallback(() => {
    if (pagination.hasPrevPage) {
      goToPage(pagination.currentPage - 1);
    }
  }, [pagination, goToPage]);

  const setPageLimit = useCallback((limit) => {
    setPagination(prev => ({ ...prev, limit }));
  }, []);

  // Sorting handlers
  const updateSorting = useCallback((field, order) => {
    setSortBy(field);
    if (order) setSortOrder(order);
  }, []);

  // Filter handlers
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleAddComment = async ({ comment, subFormId, requestsProgressId, fileId = null, fileData = null }) => {
    setAddingComment(true);
    try {
      const requestData = {
        subFormId,
        comment,
        requestsProgressId,
      };
      
      // Add file ID if provided
      if (fileId) {
        requestData.additional_file_id = fileId;
      }

      const response = await axiosInstance.post(`/api/comments/${addCommentLink}`, requestData);
      
      // Enrich the response with file data if available
      // set isOwner to true since the current user just created this comment
      const enrichedComment = { ...response.data, isOwner: true };
      if (fileData && fileId) {
        // Convert fileData to the expected additional_file format
        enrichedComment.additional_file = {
          id: fileData.id,
          original_name: fileData.original_name,
          file_path: null, // Not provided by upload response
          file_size: fileData.file_size,
          mime_type: fileData.mime_type,
          createdAt: fileData.created_at,
          updatedAt: fileData.created_at,
          deletedAt: null,
          user_id: null, // Will be set by backend
          end_user_id: null, // Will be set by backend
          ...fileData // Include any additional fields
        };
      }
      
      // Optimistically add the new comment to the list if sorting by newest first
      if (sortBy === 'createdAt' && sortOrder === 'DESC' && pagination.currentPage === 1) {
        // Add the enriched comment at the beginning
        setPrevComments(prev => [enrichedComment, ...prev].slice(0, pagination.limit));
        
        // Update pagination count
        setPagination(prev => ({
          ...prev,
          totalCount: prev.totalCount + 1
        }));
        
        // No need for background sync - the optimistic update is sufficient
        // The next page navigation or filter change will sync with server
      } else {
        // For other sorting/pages, do a regular refresh
        await fetchComments(pagination.currentPage, { limit: pagination.limit, sortBy, sortOrder, filters });
      }
      
      return enrichedComment;
    } catch (error) {
      console.error("Error adding comment:", error);
      throw error;
    } finally {
      setAddingComment(false);
    }
  };

  // edit comment handler
  const handleEditComment = async (commentId, newText, attachmentInfo = {}) => {
    try {
      const editEndpoint = persona === "customer"
        ? `/api/comments/enduser/${commentId}`
        : `/api/comments/${commentId}`;

      // build request data
      const requestData = { comment: newText };
      
      // handle attachment changes
      if (attachmentInfo.remove_attachment) {
        requestData.remove_attachment = true;
      } else if (attachmentInfo.new_file_id) {
        requestData.additional_file_id = attachmentInfo.new_file_id;
      }

      const response = await axiosInstance.put(editEndpoint, requestData);

      // update comment in local state with isOwner flag preserved
      const updatedComment = { ...response.data, isOwner: true };
      
      // if we have new file data, add it to the response
      if (attachmentInfo.new_file_data && !updatedComment.additional_file) {
        updatedComment.additional_file = {
          id: attachmentInfo.new_file_data.id,
          original_name: attachmentInfo.new_file_data.original_name,
          file_size: attachmentInfo.new_file_data.file_size,
          mime_type: attachmentInfo.new_file_data.mime_type,
        };
        updatedComment.additional_file_id = attachmentInfo.new_file_data.id;
      }

      setPrevComments(prev =>
        prev.map(c => (c.id === commentId ? updatedComment : c))
      );

      return updatedComment;
    } catch (error) {
      console.error("Error editing comment:", error);
      throw error;
    }
  };

  // delete comment handler
  const handleDeleteComment = async (commentId) => {
    try {
      const deleteEndpoint = persona === "customer"
        ? `/api/comments/enduser/${commentId}`
        : `/api/comments/${commentId}`;

      await axiosInstance.delete(deleteEndpoint);

      // remove comment from local state
      setPrevComments(prev => prev.filter(c => c.id !== commentId));

      // update pagination count
      setPagination(prev => ({
        ...prev,
        totalCount: Math.max(0, prev.totalCount - 1)
      }));
    } catch (error) {
      console.error("Error deleting comment:", error);
      throw error;
    }
  };

  return {
    // Comment data
    prevComments,
    setPrevComments,
    loadingComments,
    addingComment,
    
    // Pagination
    pagination,
    goToPage,
    nextPage,
    prevPage,
    setPageLimit,
    
    // Sorting
    sortBy,
    sortOrder,
    updateSorting,
    
    // Filtering
    filters,
    updateFilters,
        
    // Actions
    handleAddComment,
    handleEditComment,
    handleDeleteComment,
    fetchComments,
  };
}; 